import React from "react";
import {
  Box,
  Paper,
  IconButton,
  Collapse,
  Typography,
  Button,
  alpha,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CheckIcon from "@mui/icons-material/Check";
import { AutoComplete } from "../../../shared/components";
import { FilterKey, Filters } from "../../../shared/context/FilterContext";

interface MapFiltersProps {
  filters: Filters;
  tempFilters: Filters;
  updateFilter: (key: FilterKey, value: string) => void;
  applyFilters: () => void;
  resetFilters: () => void;
  hasActiveFilters: boolean;
  projectCount: number;
}

export const MapFilters: React.FC<MapFiltersProps> = ({
  filters,
  tempFilters,
  updateFilter,
  applyFilters,
  resetFilters,
  hasActiveFilters,
  projectCount,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  const hasPendingChanges =
    JSON.stringify(tempFilters) !== JSON.stringify(filters);

  const handleApplyAndClose = () => {
    applyFilters();
  };

  return (
    <Paper
      elevation={4}
      sx={{
        position: "absolute",
        top: 16,
        right: 16,
        zIndex: 10,
        borderRadius: 2,
        overflow: "hidden",
        minWidth: isExpanded ? 320 : "auto",
        backgroundColor: (theme) =>
          theme.palette.mode === "dark"
            ? alpha(theme.palette.background.paper, 0.95)
            : alpha(theme.palette.background.paper, 0.98),
        backdropFilter: "blur(8px)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 1.5,
          borderBottom: isExpanded ? "1px solid" : "none",
          borderColor: "divider",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => setIsExpanded(!isExpanded)}
            sx={{
              bgcolor: hasActiveFilters ? "primary.main" : "action.hover",
              color: hasActiveFilters ? "primary.contrastText" : "inherit",
              "&:hover": {
                bgcolor: hasActiveFilters ? "primary.dark" : "action.selected",
              },
            }}
          >
            <FilterListIcon fontSize="small" />
          </IconButton>
          {!isExpanded && (
            <Typography variant="body2" color="text.secondary">
              {projectCount} projects
            </Typography>
          )}
        </Box>

        {isExpanded && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="subtitle2">Filters</Typography>
            <IconButton size="small" onClick={() => setIsExpanded(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>

      <Collapse in={isExpanded}>
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <AutoComplete
            value={tempFilters.programme}
            onSelect={(value) => updateFilter("programme", value)}
            name="programme"
            label="Programme"
          />

          <AutoComplete
            value={tempFilters.region}
            onSelect={(value) => updateFilter("region", value)}
            name="region"
            label="Network"
          />

          <AutoComplete
            value={tempFilters.site}
            onSelect={(value) => updateFilter("site", value)}
            name="site"
            label="Site"
          />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pt: 1,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {projectCount} projects found
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                size="small"
                startIcon={<RestartAltIcon />}
                onClick={resetFilters}
                disabled={!hasActiveFilters && !hasPendingChanges}
              >
                Reset
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<CheckIcon />}
                onClick={handleApplyAndClose}
                disabled={!hasPendingChanges}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};
